import { TaxJournalDocument } from 'tax-register-core';
import {
  PRRO_LOG_LEVELS,
  PrroLogEntry,
  PrroLogLevel,
} from 'src/app/services/prro-log-queue.service';
import { isFakePrro } from './fake-prro';
import {
  countOpenOfflineSessionDocuments,
  needsRecoveryMode,
} from './tax-recovery';
import type {
  TaxRegisterContext,
  TaxRegisterContextState,
} from './tax-register-context.service';

// A log entry as the server takes it: the tenant is the request's.
export type PrroLogRequest = Omit<PrroLogEntry, 'tenantId' | 'size'>;

// A tax register's state as Nt.Pos.Api takes it (PUT prro/registers/{fiscalNumber}/state).
export interface PrroRegisterState {
  tin?: string;
  ipn?: string;
  organizationName?: string;
  registerName?: string;
  localNumber?: number;
  shiftOpened: boolean;
  zReportPresent: boolean;
  offline: boolean;
  offlineSessionOpened: boolean;
  offlineSessionId?: string;
  nextLocalNum?: number;
  offlineNextLocalNum?: number;
  // Documents of the open offline session, not known to be on the tax server.
  offlineDocuments: number;
  // A fakePrro context, or a fakePrro offline session open.
  fakePrro: boolean;
  recoveryMode: boolean;
  disabled: boolean;
  closed: boolean;
  testing: boolean;
  lastDocumentAt?: string;
  appVersion?: string;
}

// What the state takes from outside the register's context.
export interface PrroRegisterStateSources {
  disabled: boolean;
  // The open offline session was started by fakePrro.
  fakeSession: boolean;
  // The tax_recovery_mode setting.
  recoveryMode: boolean;
  appVersion?: string;
}

// How many entries the device keeps until they are sent, and for how long.
export interface PrroLogLimits {
  maxEntries: number;
  // Characters of the messages and the properties.
  maxCharacters: number;
  maxAgeMs: number;
}

export function toPrroRegisterState(
  context: TaxRegisterContext,
  state: TaxRegisterContextState,
  documents: TaxJournalDocument[],
  sources: PrroRegisterStateSources,
): PrroRegisterState {
  const config = context.taxConfig;
  const lastDocument = documents[documents.length - 1];
  return {
    tin: text(config?.tin),
    ipn: text(config?.ipn),
    organizationName: text(config?.orgFullName),
    registerName: text(config?.taxRegister?.name),
    localNumber: toNumber(config?.taxRegister?.localNumber),
    shiftOpened: Number(state.ShiftState) === 1,
    zReportPresent: !!state.ZRepPresent,
    offline: !!state.Offline,
    offlineSessionOpened: !!state.OfflineSession,
    offlineSessionId: text(state.OfflineSessionId),
    nextLocalNum: toNumber(state.NextLocalNum),
    offlineNextLocalNum: toNumber(state.OfflineNextLocalNum),
    offlineDocuments: countOpenOfflineSessionDocuments(documents),
    fakePrro: isFakePrro(context.registerState) || sources.fakeSession,
    recoveryMode:
      sources.recoveryMode ||
      needsRecoveryMode(context.registerState, documents),
    disabled: sources.disabled,
    closed: !!state.Closed,
    testing: !!state.Testing,
    lastDocumentAt: toIsoDate(lastDocument?.DocDateTime),
    appVersion: text(sources.appVersion),
  };
}

export function isPrroLogLevelEnabled(
  level: PrroLogLevel,
  minimum: PrroLogLevel,
): boolean {
  return PRRO_LOG_LEVELS.indexOf(level) >= PRRO_LOG_LEVELS.indexOf(minimum);
}

// The entries kept: none older than maxAgeMs; above the other limits the oldest Information
// entries go first, then the oldest of the others.
export function trimPrroLogEntries(
  entries: PrroLogEntry[],
  now: Date,
  limits: PrroLogLimits,
): PrroLogEntry[] {
  const since = now.getTime() - limits.maxAgeMs;
  const kept = entries.filter((entry) => !(Date.parse(entry.timestamp) < since));
  let count = kept.length;
  let characters = kept.reduce((total, entry) => total + entrySize(entry), 0);
  const removed = new Set<PrroLogEntry>();
  for (const informationOnly of [true, false]) {
    for (const entry of kept) {
      if (count <= limits.maxEntries && characters <= limits.maxCharacters) {
        break;
      }
      if (removed.has(entry) || (informationOnly && entry.level !== 'Information')) {
        continue;
      }
      removed.add(entry);
      count--;
      characters -= entrySize(entry);
    }
  }
  return removed.size ? kept.filter((entry) => !removed.has(entry)) : kept;
}

export function toPrroLogRequest(entry: PrroLogEntry): PrroLogRequest {
  const { tenantId, size, ...request } = entry;
  return request;
}

function entrySize(entry: PrroLogEntry): number {
  return (
    entry.size ??
    (entry.message?.length || 0) +
      (entry.properties ? JSON.stringify(entry.properties).length : 0)
  );
}

function text(value: unknown): string | undefined {
  return value === null || value === undefined
    ? undefined
    : String(value).trim() || undefined;
}

function toNumber(value: unknown): number | undefined {
  if (value === null || value === undefined || value === '') {
    return undefined;
  }
  const number = Number(value);
  return Number.isFinite(number) ? number : undefined;
}

// A journal date (the document's local time, without an offset) as UTC.
function toIsoDate(value: string | undefined): string | undefined {
  const time = value ? Date.parse(value) : NaN;
  return Number.isFinite(time) ? new Date(time).toISOString() : undefined;
}
